import React, { useState, useEffect, useMemo } from 'react';
import { useAlert } from '../../providers/AlertProvider';
import logger from "../../utils/logger";
import { getWarehouseProductReport } from '../../api/warehouse';
import { fetchInboundReports, fetchDispatchReports } from '../../api/enhancedWarehouse';

// Import reusable report components
import InboundReport from './report_components/InboundReport';
import DispatchReport from './report_components/DispatchReport';
import Button from './Button';

/**
 * WarehouseReports.jsx
 * 
 * Warehouse reporting component with customer restrictions
 * Shows product pallet counts and quantities per customer
 */
export default function WarehouseReports({ 
    theme = "light", 
    customers = [], 
    selectedCustomerId = null, // Use selectedCustomer from warehouse management
    isCustomer = false, 
    isAdmin = false, 
    isWarehouse = false,
    profile = null 
}) {
    const { showAlert } = useAlert();
    
    // State management
    const [selectedCustomer, setSelectedCustomer] = useState('');
    const [reportType, setReportType] = useState('warehouse'); // 'warehouse', 'inbound', 'dispatch'
    const [reportData, setReportData] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    
    // Date range state for inbound/dispatch reports
    const [dateRange, setDateRange] = useState({
        startDate: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0], // 7 days ago
        endDate: new Date().toISOString().split('T')[0] // today
    });
    
    // Report data for different types
    const [inboundReportsData, setInboundReportsData] = useState([]);
    const [dispatchReportsData, setDispatchReportsData] = useState([]);

    // Theme colors
    const colors = {
        light: {
            bg: '#ffffff',
            text: '#333333',
            border: '#dee2e6',
            primary: '#007bff',
            success: '#28a745',
            warning: '#ffc107',
            danger: '#dc3545',
            secondary: '#6c757d',
            cardBg: '#f8f9fa',
            headerBg: '#e9ecef'
        },
        dark: {
            bg: '#2d3748',
            text: '#ffffff',
            border: '#4a5568',
            primary: '#63b3ed',
            success: '#68d391',
            warning: '#fbd38d',
            danger: '#fc8181',
            secondary: '#a0aec0',
            cardBg: '#1a202c',
            headerBg: '#2d3748'
        }
    };

    const color = colors[theme] || colors.light;


    // Filter customers based on user role - same logic as warehouse page
    const availableCustomers = useMemo(() => {
        if (isCustomer && profile?.customer_id) {
            // Customer users can only see their own company
            return customers.filter(customer => customer.id === profile.customer_id);
        }
        // Admin and warehouse users can see all customers
        return customers;
    }, [customers, isCustomer, profile?.customer_id]);

    // Debug logging
    useEffect(() => {

    }, [customers, availableCustomers, isAdmin, isWarehouse, profile]);

    // Sync selected customer with prop from warehouse management
    useEffect(() => {
        if (selectedCustomerId) {
            const customerIdStr = selectedCustomerId.toString();
            setSelectedCustomer(customerIdStr);
            
            // Automatically generate the report when a customer is selected from warehouse management
            setTimeout(() => {
                fetchWarehouseReport(customerIdStr);
            }, 100);
        } else if (isCustomer && profile?.customer_id && availableCustomers.length > 0) {
            // Auto-select customer for customer users and automatically generate report
            const customerToSelect = profile.customer_id.toString();
            setSelectedCustomer(customerToSelect);
            
            // Automatically generate the report for customer users

            setTimeout(() => {
                fetchWarehouseReport(customerToSelect);
            }, 100);
        }
    }, [selectedCustomerId, isCustomer, profile?.customer_id, availableCustomers]);

    // Additional effect to ensure customer users always have their report auto-generated
    // This covers cases where the user navigates to reports tab after initial load
    useEffect(() => {
        if (isCustomer && profile?.customer_id && !selectedCustomerId && !reportData) {
            const customerToSelect = profile.customer_id.toString();

            
            // Only auto-generate if we don't already have report data and no customer is selected from warehouse management
            if (selectedCustomer !== customerToSelect) {
                setSelectedCustomer(customerToSelect);
            }
            
            // Generate report if not already loading and no report data exists
            if (!isLoading) {
                setTimeout(() => {
                    fetchWarehouseReport(customerToSelect);
                }, 200);
            }
        }
    }, [isCustomer, profile?.customer_id, selectedCustomerId, reportData, isLoading, selectedCustomer]);

    const fetchWarehouseReport = async (customerId) => {
        if (!customerId) {
            showAlert({
                message: 'Please select a customer to generate the report.',
                type: 'warning',
                buttons: [{ label: 'Close', type: 'primary' }]
            });
            return;
        }

        setIsLoading(true);
        setReportData(null);

        try {
            const response = await getWarehouseProductReport(customerId);
            
            if (response.success) {
                setReportData(response.data);
            } else {
                throw new Error(response.message || 'Failed to fetch warehouse report');
            }
            
        } catch (error) {
            logger.error('Error fetching warehouse report:', error);
            showAlert({
                message: `Failed to generate report: ${error.message}`,
                type: 'danger',
                buttons: [{ label: 'Close', type: 'primary' }]
            });
        } finally {
            setIsLoading(false);
        }
    };

    const handleGenerateReport = () => {
        if (reportType === 'warehouse') {
            fetchWarehouseReport(selectedCustomer);
        } else if (reportType === 'inbound') {
            fetchCustomerInboundReports();
        } else if (reportType === 'dispatch') {
            fetchCustomerDispatchReports();
        }
    };
    
    const fetchCustomerInboundReports = async () => {
        if (!selectedCustomer) {
            showAlert({
                message: 'Please select a customer to generate the inbound report.',
                type: 'warning',
                buttons: [{ label: 'Close', type: 'primary' }]
            });
            return;
        }

        setIsLoading(true);
        setInboundReportsData([]);

        try {
            const { data, status, success, message } = await fetchInboundReports(
                dateRange.startDate, 
                dateRange.endDate, 
                selectedCustomer // Pass customer ID for filtering
            );
            
            if (status === 200 && success) {
                setInboundReportsData(data || []);
            } else {
                throw new Error(message || 'Failed to fetch inbound reports');
            }
            
        } catch (error) {
            logger.error('Error fetching customer inbound reports:', error);
            showAlert({
                message: `Failed to generate inbound report: ${error.message}`,
                type: 'danger',
                buttons: [{ label: 'Close', type: 'primary' }]
            });
        } finally {
            setIsLoading(false);
        }
    };

    const fetchCustomerDispatchReports = async () => {
        if (!selectedCustomer) {
            showAlert({
                message: 'Please select a customer to generate the dispatch report.',
                type: 'warning',
                buttons: [{ label: 'Close', type: 'primary' }]
            });
            return;
        }

        setIsLoading(true);
        setDispatchReportsData([]);

        try {
            const { data, status, success, message } = await fetchDispatchReports(
                dateRange.startDate, 
                dateRange.endDate, 
                selectedCustomer // Pass customer ID for filtering
            );
            
            if (success && status === 200 && data) {
                setDispatchReportsData(data || []);
            } else {
                throw new Error(message || 'Failed to fetch dispatch reports');
            }
            
        } catch (error) {
            logger.error('Error fetching customer dispatch reports:', error);
            showAlert({
                message: `Failed to generate dispatch report: ${error.message}`,
                type: 'danger',
                buttons: [{ label: 'Close', type: 'primary' }]
            });
        } finally {
            setIsLoading(false);
        }
    };

    const formatNumber = (num) => {
        return new Intl.NumberFormat().format(num || 0);
    };

    const renderProductTable = () => {
        if (!reportData?.products || reportData.products.length === 0) {
            return (
                <div style={{
                    backgroundColor: color.cardBg,
                    border: `1px solid ${color.border}`,
                    borderRadius: '6px',
                    padding: '2rem',
                    textAlign: 'center',
                    marginTop: '1rem'
                }}>
                    <p style={{ color: color.secondary, margin: 0 }}>
                        No products found for the selected customer.
                    </p>
                </div>
            );
        }

        return (
            <div style={{
                backgroundColor: color.cardBg,
                border: `1px solid ${color.border}`,
                borderRadius: '6px',
                overflow: 'hidden',
                marginTop: '1rem'
            }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead>
                        <tr style={{ backgroundColor: color.headerBg }}>
                            <th style={{ 
                                padding: '1rem', 
                                textAlign: 'left', 
                                color: color.text,
                                borderBottom: `1px solid ${color.border}`,
                                fontWeight: '600'
                            }}>
                                Product Name
                            </th>
                            <th style={{ 
                                padding: '1rem', 
                                textAlign: 'center', 
                                color: color.text,
                                borderBottom: `1px solid ${color.border}`,
                                fontWeight: '600'
                            }}>
                                SKU
                            </th>
                            <th style={{ 
                                padding: '1rem', 
                                textAlign: 'center', 
                                color: color.text,
                                borderBottom: `1px solid ${color.border}`,
                                fontWeight: '600'
                            }}>
                                Pallet Count
                            </th>
                            <th style={{ 
                                padding: '1rem', 
                                textAlign: 'center', 
                                color: color.text,
                                borderBottom: `1px solid ${color.border}`,
                                fontWeight: '600'
                            }}>
                                Total Quantity
                            </th>
                            <th style={{ 
                                padding: '1rem', 
                                textAlign: 'center', 
                                color: color.text,
                                borderBottom: `1px solid ${color.border}`,
                                fontWeight: '600'
                            }}>
                                Avg per Pallet
                            </th>
                        </tr>
                    </thead>
                    <tbody>
                        {reportData.products.map((product, index) => {
                            const avgPerPallet = product.palletCount > 0 
                                ? Math.round(product.totalQuantity / product.palletCount)
                                : 0;

                            return (
                                <tr key={`${product.productId}-${index}`} style={{
                                    backgroundColor: index % 2 === 0 ? color.bg : color.cardBg
                                }}>
                                    <td style={{ 
                                        padding: '1rem', 
                                        color: color.text,
                                        borderBottom: `1px solid ${color.border}`,
                                        fontWeight: '500'
                                    }}>
                                        {product.productName || 'Unknown Product'}
                                    </td>
                                    <td style={{ 
                                        padding: '1rem', 
                                        textAlign: 'center', 
                                        color: color.secondary,
                                        borderBottom: `1px solid ${color.border}`,
                                        fontFamily: 'monospace'
                                    }}>
                                        {product.sku || 'N/A'}
                                    </td>
                                    <td style={{ 
                                        padding: '1rem', 
                                        textAlign: 'center', 
                                        color: color.primary,
                                        borderBottom: `1px solid ${color.border}`,
                                        fontWeight: 'bold'
                                    }}>
                                        {formatNumber(product.palletCount)}
                                    </td>
                                    <td style={{ 
                                        padding: '1rem', 
                                        textAlign: 'center', 
                                        color: color.success,
                                        borderBottom: `1px solid ${color.border}`,
                                        fontWeight: 'bold'
                                    }}>
                                        {formatNumber(product.totalQuantity)}
                                    </td>
                                    <td style={{ 
                                        padding: '1rem', 
                                        textAlign: 'center', 
                                        color: color.text,
                                        borderBottom: `1px solid ${color.border}`
                                    }}>
                                        {formatNumber(avgPerPallet)}
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>
        );
    };

    const renderSummaryCards = () => {
        if (!reportData) return null;

        return (
            <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                gap: '1rem',
                marginBottom: '2rem'
            }}>
                <div style={{
                    backgroundColor: color.cardBg,
                    border: `1px solid ${color.border}`,
                    borderRadius: '6px',
                    padding: '1rem',
                    textAlign: 'center'
                }}>
                    <h5 style={{ color: color.primary, margin: '0 0 0.5rem 0' }}>
                        Total Products
                    </h5>
                    <div style={{ fontSize: '2rem', fontWeight: 'bold', color: color.text }}>
                        {formatNumber(reportData.summary?.totalProducts || 0)}
                    </div>
                </div>

                <div style={{
                    backgroundColor: color.cardBg,
                    border: `1px solid ${color.border}`,
                    borderRadius: '6px',
                    padding: '1rem',
                    textAlign: 'center'
                }}>
                    <h5 style={{ color: color.success, margin: '0 0 0.5rem 0' }}>
                        Total Pallets
                    </h5>
                    <div style={{ fontSize: '2rem', fontWeight: 'bold', color: color.text }}>
                        {formatNumber(reportData.summary?.totalPallets || 0)}
                    </div>
                </div>

                <div style={{
                    backgroundColor: color.cardBg,
                    border: `1px solid ${color.border}`,
                    borderRadius: '6px',
                    padding: '1rem',
                    textAlign: 'center'
                }}>
                    <h5 style={{ color: color.warning, margin: '0 0 0.5rem 0' }}>
                        Total Quantity
                    </h5>
                    <div style={{ fontSize: '2rem', fontWeight: 'bold', color: color.text }}>
                        {formatNumber(reportData.summary?.totalQuantity || 0)}
                    </div>
                </div>

                <div style={{
                    backgroundColor: color.cardBg,
                    border: `1px solid ${color.border}`,
                    borderRadius: '6px',
                    padding: '1rem',
                    textAlign: 'center'
                }}>
                    <h5 style={{ color: color.secondary, margin: '0 0 0.5rem 0' }}>
                        Avg per Pallet
                    </h5>
                    <div style={{ fontSize: '2rem', fontWeight: 'bold', color: color.text }}>
                        {formatNumber(reportData.summary?.avgPerPallet || 0)}
                    </div>
                </div>
            </div>
        );
    };

    return (
        <div style={{
            padding: '1rem',
            color: color.text,
            minHeight: '500px'
        }}>
            <h3 style={{ 
                marginBottom: '2rem', 
                color: color.text,
                textAlign: 'center'
            }}>
                Warehouse Reports
            </h3>

            {/* Report Configuration */}
            <div style={{
                backgroundColor: color.cardBg,
                border: `1px solid ${color.border}`,
                borderRadius: '8px',
                padding: '1.5rem',
                marginBottom: '1rem'
            }}>
                <h5 style={{ color: color.text, marginBottom: '1rem' }}>
                    Report Configuration
                    {isCustomer && (
                        <div style={{
                            fontSize: '0.9rem',
                            color: color.primary,
                            fontWeight: 'normal',
                            marginTop: '0.5rem'
                        }}>
                            🔐 Automatically showing report for your company only
                        </div>
                    )}
                </h5>
                
                <div style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
                    gap: '1rem',
                    alignItems: 'end'
                }}>
                    {/* Customer Selection */}
                    <div>
                        <label style={{ 
                            display: 'block', 
                            marginBottom: '0.5rem', 
                            color: color.text,
                            fontWeight: '500'
                        }}>
                            Customer:
                            {isCustomer && (
                                <span style={{ 
                                    fontSize: '0.8rem', 
                                    color: color.secondary,
                                    fontWeight: 'normal',
                                    marginLeft: '0.5rem'
                                }}>
                                    (Restricted to your company)
                                </span>
                            )}
                            {selectedCustomerId && !isCustomer && (
                                <span style={{ 
                                    fontSize: '0.8rem', 
                                    color: color.primary,
                                    fontWeight: 'normal',
                                    marginLeft: '0.5rem'
                                }}>
                                    (Selected from warehouse management)
                                </span>
                            )}
                        </label>
                        <select
                            value={selectedCustomer}
                            onChange={(e) => setSelectedCustomer(e.target.value)}
                            disabled={isCustomer || selectedCustomerId} // Disable if customer user or controlled by warehouse management
                            style={{
                                width: '100%',
                                padding: '0.75rem',
                                border: `1px solid ${color.border}`,
                                borderRadius: '4px',
                                backgroundColor: color.bg,
                                color: color.text,
                                fontSize: '1rem',
                                opacity: (isCustomer || selectedCustomerId) ? 0.7 : 1
                            }}
                        >
                            <option value="">
                                {availableCustomers.length === 0 
                                    ? 'No customers with warehouse space...' 
                                    : 'Select Customer...'
                                }
                            </option>
                            {availableCustomers.map(customer => (
                                <option key={customer.id} value={customer.id}>
                                    {customer.company_name || customer.company || `Customer ${customer.id}`}
                                </option>
                            ))}
                        </select>
                        {availableCustomers.length === 0 && (
                            <div style={{ 
                                fontSize: '0.8rem', 
                                color: color.secondary,
                                marginTop: '0.5rem',
                                fontStyle: 'italic'
                            }}>
                                No customers have been allocated warehouse space yet.
                            </div>
                        )}
                    </div>

                    {/* Report Type Selection */}
                    <div>
                        <label style={{ 
                            display: 'block', 
                            marginBottom: '0.5rem', 
                            color: color.text,
                            fontWeight: '500'
                        }}>
                            Report Type:
                        </label>
                        <select
                            value={reportType}
                            onChange={(e) => {
                                setReportType(e.target.value);
                                // Clear previous report data when changing type
                                setReportData(null);
                                setInboundReportsData([]);
                                setDispatchReportsData([]);
                            }}
                            style={{
                                width: '100%',
                                padding: '0.75rem',
                                border: `1px solid ${color.border}`,
                                borderRadius: '4px',
                                backgroundColor: color.bg,
                                color: color.text,
                                fontSize: '1rem'
                            }}
                        >
                            <option value="warehouse">Warehouse Stock Report</option>
                            <option value="inbound">Inbound Activity Report</option>
                            <option value="dispatch">Dispatch Activity Report</option>
                        </select>
                    </div>

                    {/* Date Range for Inbound/Dispatch Reports */}
                    {(reportType === 'inbound' || reportType === 'dispatch') && (
                        <>
                            <div>
                                <label style={{ 
                                    display: 'block', 
                                    marginBottom: '0.5rem', 
                                    color: color.text,
                                    fontWeight: '500'
                                }}>
                                    Start Date:
                                </label>
                                <input
                                    type="date"
                                    value={dateRange.startDate}
                                    onChange={(e) => setDateRange(prev => ({ ...prev, startDate: e.target.value }))}
                                    style={{
                                        width: '100%',
                                        padding: '0.75rem',
                                        border: `1px solid ${color.border}`,
                                        borderRadius: '4px',
                                        backgroundColor: color.bg,
                                        color: color.text,
                                        fontSize: '1rem'
                                    }}
                                />
                            </div>
                            <div>
                                <label style={{ 
                                    display: 'block', 
                                    marginBottom: '0.5rem', 
                                    color: color.text,
                                    fontWeight: '500'
                                }}>
                                    End Date:
                                </label>
                                <input
                                    type="date"
                                    value={dateRange.endDate}
                                    onChange={(e) => setDateRange(prev => ({ ...prev, endDate: e.target.value }))}
                                    style={{
                                        width: '100%',
                                        padding: '0.75rem',
                                        border: `1px solid ${color.border}`,
                                        borderRadius: '4px',
                                        backgroundColor: color.bg,
                                        color: color.text,
                                        fontSize: '1rem'
                                    }}
                                />
                            </div>
                        </>
                    )}

                    {/* Generate Button */}
                    {!isCustomer && (
                        <div>
                            <button
                                onClick={handleGenerateReport}
                                disabled={isLoading || !selectedCustomer}
                                style={{
                                    width: '100%',
                                    padding: '0.75rem 1.5rem',
                                    backgroundColor: isLoading || !selectedCustomer ? color.secondary : color.primary,
                                    color: '#ffffff',
                                    border: 'none',
                                    borderRadius: '4px',
                                    fontSize: '1rem',
                                    fontWeight: '500',
                                    cursor: isLoading || !selectedCustomer ? 'not-allowed' : 'pointer',
                                    transition: 'background-color 0.2s'
                                }}
                            >
                                {isLoading ? 'Generating...' : 
                                    reportType === 'warehouse' ? 'Generate Stock Report' :
                                    reportType === 'inbound' ? 'Generate Inbound Report' :
                                    'Generate Dispatch Report'
                                }
                            </button>
                        </div>
                    )}
                    
                    {/* Refresh Button for Customer Users */}
                    {isCustomer && (
                        <div>
                            <button
                                onClick={handleGenerateReport}
                                disabled={isLoading || !selectedCustomer}
                                style={{
                                    width: '100%',
                                    padding: '0.75rem 1.5rem',
                                    backgroundColor: isLoading || !selectedCustomer ? color.secondary : color.success,
                                    color: '#ffffff',
                                    border: 'none',
                                    borderRadius: '4px',
                                    fontSize: '1rem',
                                    fontWeight: '500',
                                    cursor: isLoading || !selectedCustomer ? 'not-allowed' : 'pointer',
                                    transition: 'background-color 0.2s'
                                }}
                            >
                                {isLoading ? 'Refreshing...' : 
                                    reportType === 'warehouse' ? 'Refresh Stock Report' :
                                    reportType === 'inbound' ? 'Refresh Inbound Report' :
                                    'Refresh Dispatch Report'
                                }
                            </button>
                            <div style={{
                                fontSize: '0.8rem',
                                color: color.secondary,
                                marginTop: '0.5rem',
                                textAlign: 'center'
                            }}>
                                Report automatically generated for your company
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {/* Report Results */}
            {reportType === 'warehouse' && reportData && (
                <div>
                    <h4 style={{ 
                        color: color.text, 
                        marginBottom: '1.5rem',
                        borderBottom: `2px solid ${color.border}`,
                        paddingBottom: '0.5rem'
                    }}>
                        Warehouse Stock Report
                    </h4>

                    {/* Summary Cards */}
                    {renderSummaryCards()}

                    {/* Product Details Table */}
                    <h5 style={{ color: color.text, marginBottom: '1rem' }}>
                        Product Details
                    </h5>
                    {renderProductTable()}
                </div>
            )}

            {/* Inbound Reports Results */}
            {reportType === 'inbound' && inboundReportsData.length > 0 && (
                <div>
                    <h4 style={{ 
                        color: color.text, 
                        marginBottom: '1.5rem',
                        borderBottom: `2px solid ${color.border}`,
                        paddingBottom: '0.5rem'
                    }}>
                        Customer Inbound Activity Report
                    </h4>
                    <InboundReport inboundData={inboundReportsData} theme={theme} />
                </div>
            )}

            {/* Dispatch Reports Results */}
            {reportType === 'dispatch' && dispatchReportsData.length > 0 && (
                <div>
                    <h4 style={{ 
                        color: color.text, 
                        marginBottom: '1.5rem',
                        borderBottom: `2px solid ${color.border}`,
                        paddingBottom: '0.5rem'
                    }}>
                        Customer Dispatch Activity Report
                    </h4>
                    <DispatchReport dispatchData={dispatchReportsData} theme={theme} />
                </div>
            )}

            {/* No Data Messages */}
            {reportType === 'inbound' && inboundReportsData.length === 0 && !isLoading && selectedCustomer && (
                <div style={{
                    backgroundColor: color.cardBg,
                    border: `1px solid ${color.border}`,
                    borderRadius: '6px',
                    padding: '2rem',
                    textAlign: 'center',
                    marginTop: '1rem'
                }}>
                    <p style={{ color: color.secondary, margin: 0 }}>
                        No inbound activity found for the selected customer and date range.
                    </p>
                </div>
            )}

            {reportType === 'dispatch' && dispatchReportsData.length === 0 && !isLoading && selectedCustomer && (
                <div style={{
                    backgroundColor: color.cardBg,
                    border: `1px solid ${color.border}`,
                    borderRadius: '6px',
                    padding: '2rem',
                    textAlign: 'center',
                    marginTop: '1rem'
                }}>
                    <p style={{ color: color.secondary, margin: 0 }}>
                        No dispatch activity found for the selected customer and date range.
                    </p>
                </div>
            )}
        </div>
    );
}